import {Component, OnInit, Input, forwardRef, OnDestroy} from '@angular/core'
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormArray,
  Validator,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  ValidationErrors,
} from '@angular/forms'
import {map, takeUntil} from 'rxjs/operators'
import {Subject} from 'rxjs'

export interface KeyValueListInputConfig {
  keyPattern?: string
  keyValidatorErrorMessage?: string
  valuePattern?: string
  valueValidatorErrorMessage?: string
  duplicateKeyErrorMessage?: string
}

const defaultConfig: KeyValueListInputConfig = {
  keyPattern: '(.*?)', // anything
  keyValidatorErrorMessage: 'Cannot be empty',
  valuePattern: '(.*?)',
  valueValidatorErrorMessage: 'Cannot be empty',
  duplicateKeyErrorMessage: 'Duplicate keys not allowed',
}

@Component({
  selector: 'app-key-value-list-input',
  templateUrl: './key-value-list-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KeyValueListInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => KeyValueListInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class KeyValueListInputComponent
  implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
  @Input() config: KeyValueListInputConfig = defaultConfig

  destroy$ = new Subject<boolean>()

  formGroup: FormGroup = new FormGroup({
    keyValueArray: this.formBuilder.array([]),
  })
  parsedConfig: KeyValueListInputConfig

  constructor(private formBuilder: FormBuilder) {}

  writeValue(obj: any): void {
    if (obj) {
      // empty formgroup and (re)populate
      this.formGroup.controls.keyValueArray = this.formBuilder.array([])
      if (!obj.length) {
        this.addKeyValueFormGroupToForm()
      } else {
        obj.forEach((item: {key: string; value: string}) => {
          this.addKeyValueFormGroupToForm(item)
        })
      }
    }
  }
  registerOnChange(fn: any): void {
    this.formGroup.valueChanges
      .pipe(
        map((val) => {
          return val.keyValueArray
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(fn)
  }
  registerOnTouched(fn: any): void {
    // throw new Error("Method not implemented.");
  }
  setDisabledState?(isDisabled: boolean): void {
    // throw new Error("Method not implemented.");
  }
  validate(control: FormArray): ValidationErrors {
    let customValidation = this.uniqueKeyValidator(control)
    return this.formGroup.valid && !customValidation
      ? null
      : {valid: false, message: customValidation ? customValidation.error : ''}
  }
  registerOnValidatorChange?(fn: () => void): void {
    this.formGroup.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(fn)
  }

  addKeyValueFormGroupToForm({key = '', value = ''} = {}) {
    ;(this.formGroup.get('keyValueArray') as FormArray).push(
      this.createKeyValueForm({key: key, value: value}),
    )
  }

  removeKeyValueFormGroupFromForm(i) {
    ;(this.formGroup.get('keyValueArray') as FormArray).removeAt(i)
  }

  createKeyValueForm({key = '', value = ''}): FormGroup {
    return this.formBuilder.group({
      key: [
        key,
        [Validators.required, Validators.pattern(this.parsedConfig.keyPattern)],
      ],
      value: [
        value,
        [
          Validators.required,
          Validators.pattern(this.parsedConfig.valuePattern),
        ],
      ],
    })
  }

  uniqueKeyValidator(formArray: FormArray) {
    if (!formArray.value) {
      return null
    }
    let arrKeys = formArray.value
      .map((item) => item.key)
      .filter((k) => k !== '')
    if (arrKeys.length !== Array.from(new Set(arrKeys)).length) {
      return {error: this.parsedConfig.duplicateKeyErrorMessage}
    }
    return null
  }

  ngOnInit() {
    this.parsedConfig = Object.assign(defaultConfig, this.config)
    this.addKeyValueFormGroupToForm()
  }

  ngOnDestroy(): void {
    this.destroy$.next(true)
    this.destroy$.unsubscribe()
  }
}
